import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Mail, Globe, Loader2, CheckCircle2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { INTEREST_OPTIONS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", company: "", interest: "", message: "" };

const readPrefill = () => {
  try {
    const v = new URLSearchParams(window.location.search).get("service");
    return v && INTEREST_OPTIONS.includes(v) ? v : "";
  } catch (e) {
    return "";
  }
};

export const ContactSection = () => {
  const [form, setForm] = useState(() => ({ ...initial, interest: readPrefill() }));
  const [prefilled, setPrefilled] = useState(() => !!readPrefill());
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        interest: form.interest || null,
        message: form.message.trim(),
      });
      setSent(true);
      toast.success("Enquiry sent — the Farmint team will get back to you.");
    } catch (err) {
      toast.error("Could not send your enquiry. Please email hello@farmint.in directly.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border hairline bg-background/70 px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground/70 outline-none transition-colors duration-200 focus:border-primary/60 focus:ring-2 focus:ring-primary/20";

  return (
    <section id="contact" className="relative overflow-hidden py-24 sm:py-32" data-testid="contact-section">
      <div className="absolute inset-0 green-glow-bg" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-primary font-semibold">11 / Contact</p>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight">
                Have a software idea or <span className="text-primary">engineering challenge?</span>
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-4 max-w-md text-base md:text-lg text-muted-foreground leading-relaxed">
                Tell us what you&apos;re building. Let&apos;s explore how Farmint can help you
                design, build and scale it.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <div className="mt-8 space-y-3">
                <a
                  href="mailto:hello@farmint.in"
                  data-testid="contact-email-link"
                  className="flex w-fit items-center gap-3 rounded-full border hairline px-5 py-2.5 text-sm font-medium text-foreground/85 transition-colors duration-200 hover:border-primary/50 hover:text-primary"
                >
                  <Mail className="h-4 w-4 text-primary" /> hello@farmint.in
                </a>
                <a
                  href="https://farmint.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-website-link"
                  className="flex w-fit items-center gap-3 rounded-full border hairline px-5 py-2.5 text-sm font-medium text-foreground/85 transition-colors duration-200 hover:border-primary/50 hover:text-primary"
                >
                  <Globe className="h-4 w-4 text-primary" /> farmint.in
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div className="rounded-2xl border hairline bg-card/70 p-7 sm:p-9">
              {sent ? (
                <div className="flex min-h-[380px] flex-col items-center justify-center text-center" data-testid="contact-success">
                  <div className="rounded-full border border-primary/40 bg-primary/10 p-4">
                    <CheckCircle2 className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="mt-6 font-display text-xl font-bold">Conversation started.</h3>
                  <p className="mt-2 max-w-xs text-sm text-muted-foreground">
                    Your enquiry has been received. The Farmint team will reply to{" "}
                    <span className="text-foreground">{form.email}</span> shortly.
                  </p>
                  <button
                    onClick={() => { setSent(false); setForm(initial); }}
                    data-testid="contact-send-another-btn"
                    className="mt-6 font-mono text-xs uppercase tracking-[0.18em] text-primary hover:underline"
                  >
                    Send another enquiry
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                  {prefilled && form.interest && (
                    <div className="flex items-center justify-between rounded-xl border border-primary/30 bg-primary/[0.06] px-4 py-2.5" data-testid="contact-prefill-chip">
                      <p className="text-xs text-foreground/85">
                        Enquiring about <span className="font-semibold text-primary">{form.interest}</span>
                      </p>
                      <button
                        type="button"
                        aria-label="Clear preselected service"
                        data-testid="contact-prefill-clear-btn"
                        onClick={() => { setPrefilled(false); setForm((f) => ({ ...f, interest: "" })); }}
                        className="font-mono text-xs text-muted-foreground transition-colors hover:text-foreground"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <input required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} data-testid="contact-name-input" aria-label="Your name" />
                    <input required type="email" value={form.email} onChange={set("email")} placeholder="Work email" className={inputCls} data-testid="contact-email-input" aria-label="Work email" />
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <input value={form.company} onChange={set("company")} placeholder="Company (optional)" className={inputCls} data-testid="contact-company-input" aria-label="Company" />
                    <select value={form.interest} onChange={set("interest")} className={`${inputCls} appearance-none`} data-testid="contact-interest-select" aria-label="Interested in">
                      <option value="">Interested in…</option>
                      {INTEREST_OPTIONS.map((o) => (
                        <option key={o} value={o}>{o}</option>
                      ))}
                    </select>
                  </div>
                  <textarea
                    required
                    value={form.message}
                    onChange={set("message")}
                    placeholder="What are you building? A sentence or two is plenty."
                    rows={5}
                    className={`${inputCls} resize-none`}
                    data-testid="contact-message-input"
                    aria-label="Message"
                  />
                  <button
                    type="submit"
                    disabled={sending}
                    data-testid="contact-submit-btn"
                    className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground transition-all duration-200 hover:brightness-110 hover:shadow-[0_0_36px_var(--green-glow)] disabled:opacity-60"
                  >
                    {sending ? (
                      <>Sending <Loader2 className="h-4 w-4 animate-spin" /></>
                    ) : (
                      <>Talk to Farmint <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" /></>
                    )}
                  </button>
                  <p className="text-center text-xs text-muted-foreground">
                    No retainers discussed here — just your idea and how we can engineer it.
                  </p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
